import { router } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'

interface OrgPreview {
  id: number
  name: string
  current_member: boolean
  current_role: string | null
  guide_count: number
  at_guide_cap: boolean
  blockers: { rocks: number; topics: number; metrics: number; direct_reports: number } | null
}

interface Preview {
  account: { id: number; full_name: string; email: string }
  is_guide: boolean
  standalone_guide: boolean
  organizations: OrgPreview[]
}

interface Props {
  accountId: number
  accountName: string
  direction: 'to_guide' | 'to_user'
  onClose: () => void
}

function blockerLines(org: OrgPreview): string[] {
  const b = org.blockers
  if (!b) return []
  const lines: string[] = []
  if (b.rocks > 0) lines.push(`${b.rocks} active ${b.rocks === 1 ? 'Rock' : 'Rocks'}`)
  if (b.topics > 0) lines.push(`${b.topics} open ${b.topics === 1 ? 'Topic' : 'Topics'}`)
  if (b.metrics > 0) lines.push(`${b.metrics} active ${b.metrics === 1 ? 'Metric' : 'Metrics'}`)
  if (b.direct_reports > 0) lines.push(`${b.direct_reports} direct ${b.direct_reports === 1 ? 'report' : 'reports'}`)
  return lines
}

export default function RoleConversionModal({ accountId, accountName, direction, onClose }: Props) {
  const [preview, setPreview] = useState<Preview | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([])
  const [keepOrgId, setKeepOrgId] = useState<number | null>(null)
  const [newRole, setNewRole] = useState<'observer' | 'member' | 'admin'>('member')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch(`/super_admin/role_conversions/preview?account_id=${accountId}`, { headers: { Accept: 'application/json' } }) // allow-direct-fetch: read-only GET of conversion preview data
      .then(res => {
        if (!res.ok) throw new Error('preview failed')
        return res.json()
      })
      .then((data: Preview) => {
        if (cancelled) return
        setPreview(data)
        const memberOrgs = data.organizations.filter(o => o.current_member)
        if (direction === 'to_guide') {
          setSelectedOrgIds(memberOrgs.filter(o => !o.at_guide_cap).map(o => o.id))
        } else {
          setKeepOrgId(memberOrgs.length === 1 ? memberOrgs[0].id : null)
        }
      })
      .catch(() => { if (!cancelled) setLoadError(true) })
    return () => { cancelled = true }
  }, [accountId, direction])

  const memberOrgs = useMemo(
    () => (preview ? preview.organizations.filter(o => o.current_member) : []),
    [preview]
  )
  const otherOrgs = useMemo(
    () => (preview ? preview.organizations.filter(o => !o.current_member) : []),
    [preview]
  )

  const blockedOrgs = memberOrgs.filter(o => blockerLines(o).length > 0)
  const hasBlockers = direction === 'to_guide' && blockedOrgs.length > 0

  const removedOrgs =
    direction === 'to_guide'
      ? memberOrgs.filter(o => !selectedOrgIds.includes(o.id))
      : memberOrgs.filter(o => o.id !== keepOrgId)

  const canConfirm =
    !submitting &&
    preview !== null &&
    (direction === 'to_guide'
      ? selectedOrgIds.length > 0 && !hasBlockers
      : keepOrgId !== null)

  const toggleOrg = (id: number) => {
    setSelectedOrgIds(ids => (ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]))
  }

  const confirm = () => {
    if (!canConfirm) return
    setSubmitting(true)
    if (direction === 'to_guide') {
      router.post(
        '/super_admin/role_conversions/to_guide',
        { account_id: accountId, organization_ids: selectedOrgIds },
        { onFinish: () => { setSubmitting(false); onClose() } }
      )
    } else {
      router.post(
        '/super_admin/role_conversions/to_user',
        { account_id: accountId, keep_organization_id: keepOrgId, new_role: newRole },
        { onFinish: () => { setSubmitting(false); onClose() } }
      )
    }
  }

  const title =
    direction === 'to_guide'
      ? `Convert ${accountName} to a Guide?`
      : `Convert ${accountName} to a regular user?`

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-text-primary">{title}</h2>

        {loadError && (
          <p className="text-sm text-[#ED1C24] mt-3">Could not load the conversion preview. Close and try again.</p>
        )}
        {!preview && !loadError && (
          <p className="text-sm text-text-muted mt-3">Loading preview…</p>
        )}

        {preview && direction === 'to_guide' && (
          <div className="mt-3 space-y-4">
            <p className="text-sm text-text-muted">
              Select the {otherOrgs.length > 0 ? 'companies' : 'company'} where{' '}
              <strong>{preview.account.full_name}</strong> will serve as a Guide. Their current
              memberships are pre-selected.
            </p>

            {hasBlockers && (
              <div className="rounded-lg border border-[#ED1C24]/40 bg-[#FDECEC] p-3">
                <p className="text-xs font-semibold text-[#C7161E]">
                  Blockers — Guides can't own Rocks, Topics, or Metrics, or manage people. Reassign
                  these first using the normal reassignment flows:
                </p>
                <ul className="mt-2 list-disc list-inside text-xs text-text-muted space-y-0.5">
                  {blockedOrgs.map(o => (
                    <li key={o.id}>
                      <strong>{o.name}:</strong> {blockerLines(o).join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="max-h-56 overflow-y-auto border border-stone/20 rounded-lg divide-y divide-stone/10">
              {[...memberOrgs, ...otherOrgs].map(org => {
                const disabled = org.at_guide_cap
                const checked = selectedOrgIds.includes(org.id)
                return (
                  <label
                    key={org.id}
                    className={`flex items-start gap-2 px-3 py-2 text-sm ${disabled ? 'opacity-60 cursor-not-allowed bg-[#F5F5F5]' : 'cursor-pointer hover:bg-[#F5F5F5]'}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      disabled={disabled}
                      checked={checked && !disabled}
                      onChange={() => toggleOrg(org.id)}
                    />
                    <span className="min-w-0">
                      <span className="font-semibold text-text-primary">{org.name}</span>
                      {org.current_member && (
                        <span className="ml-2 text-xs text-text-muted">currently {org.current_role}</span>
                      )}
                      {disabled ? (
                        <span className="block text-xs text-[#C7161E]">
                          Already has {org.guide_count} guides — the maximum. Remove a guide there first.
                        </span>
                      ) : org.guide_count === 1 && checked ? (
                        <span className="block text-xs text-[#8A5A00]">
                          This company will now have 2 guides — the maximum.
                        </span>
                      ) : null}
                    </span>
                  </label>
                )
              })}
            </div>

            <div className="rounded-lg bg-[#F5F5F5] p-3 text-xs text-text-muted space-y-1">
              <p className="font-semibold text-text-primary">What will happen</p>
              {selectedOrgIds.length > 0 ? (
                <p>
                  Becomes a Guide in:{' '}
                  {preview.organizations.filter(o => selectedOrgIds.includes(o.id)).map(o => o.name).join(', ')}
                </p>
              ) : (
                <p className="text-[#C7161E]">Select at least one company.</p>
              )}
              {removedOrgs.length > 0 && (
                <p>
                  Removed from: {removedOrgs.map(o => o.name).join(', ')} (their login is untouched)
                </p>
              )}
            </div>
          </div>
        )}

        {preview && direction === 'to_user' && (
          <div className="mt-3 space-y-4">
            <p className="text-sm text-text-muted">
              Regular users belong to a single company. Choose the one company{' '}
              <strong>{preview.account.full_name}</strong> will stay in as a regular member.
            </p>

            {memberOrgs.length === 0 ? (
              <p className="text-sm text-[#C7161E]">
                This Guide isn't attached to any company. Add them to a tenant first, or leave them
                as a standalone Guide.
              </p>
            ) : (
              <div className="max-h-56 overflow-y-auto border border-stone/20 rounded-lg divide-y divide-stone/10">
                {memberOrgs.map(org => (
                  <label key={org.id} className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#F5F5F5]">
                    <input
                      type="radio"
                      name="keep_org"
                      className="mt-0.5"
                      checked={keepOrgId === org.id}
                      onChange={() => setKeepOrgId(org.id)}
                    />
                    <span className="min-w-0">
                      <span className="font-semibold text-text-primary">{org.name}</span>
                      <span className="ml-2 text-xs text-text-muted">currently {org.current_role}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            {memberOrgs.length > 0 && (
              <div>
                <label htmlFor="new-role-select" className="block text-sm font-semibold text-text-primary">
                  Role in the kept company
                </label>
                <select
                  id="new-role-select"
                  value={newRole}
                  onChange={e => setNewRole(e.target.value as 'observer' | 'member' | 'admin')}
                  className="mt-1 w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary bg-white"
                >
                  <option value="observer">Observer</option>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            )}

            <div className="rounded-lg bg-[#F5F5F5] p-3 text-xs text-text-muted space-y-1">
              <p className="font-semibold text-text-primary">What will happen</p>
              {keepOrgId !== null ? (
                <p>
                  Stays in <strong>{memberOrgs.find(o => o.id === keepOrgId)?.name}</strong> as a
                  regular user with the{' '}
                  <strong>{newRole.charAt(0).toUpperCase() + newRole.slice(1)}</strong> role (no
                  longer a Guide).
                </p>
              ) : (
                <p className="text-[#C7161E]">Select the one company to keep.</p>
              )}
              {removedOrgs.length > 0 && (
                <p>Removed from: {removedOrgs.map(o => o.name).join(', ')}</p>
              )}
              {preview.standalone_guide && (
                <p>Their standalone Guide record will be cleaned up.</p>
              )}
              <p>Their login and password are untouched.</p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-2 border border-stone/30 rounded-lg text-sm">Cancel</button>
          <button
            onClick={confirm}
            disabled={!canConfirm}
            className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E] disabled:opacity-50"
          >
            {direction === 'to_guide' ? 'Convert to Guide' : 'Convert to regular user'}
          </button>
        </div>
      </div>
    </div>
  )
}
